import { type FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { IconStarFilled } from '@xing-com/icons';
import type { MarkerProps, MarkerVariant } from '@xing-com/marker';

import type { SimpleCompanyItem } from '../../types';
import { TrackVisibilitySensor } from '../../utils/tracking/tracking-visibility-sensor';
import { SimpleCompanyCardSkeleton } from './simple-company-card.skeleton';
import * as Styled from './simple-company-card.styled';

type SimpleCompanyCardSubcomponents = {
  Skeleton: typeof SimpleCompanyCardSkeleton;
};
type SimpleCompanyCardProps = {
  item: SimpleCompanyItem;
  horizontal?: boolean;
  onWasSeen?: () => void;
  onClick?: () => void;
};

export const SimpleCompanyCard: FC<SimpleCompanyCardProps> &
  SimpleCompanyCardSubcomponents = ({
  item,
  horizontal,
  onWasSeen,
  onClick,
  ...rest
}) => {
  const markers: MarkerProps[] = [
    ...(item.hasOpenJobs
      ? [
          {
            children: <FormattedMessage id="CP_HIRING" />,
            variant: 'highlight' as MarkerVariant,
            ellipsis: true,
          },
        ]
      : []),
    ...(item.kununuRatting
      ? [
          {
            children: item.kununuRatting,
            icon: IconStarFilled,
          },
        ]
      : []),
  ];
  return (
    <TrackVisibilitySensor onWasSeen={onWasSeen}>
      <Styled.EntityCard
        key={item.id}
        data-testid={`entity-card-${item.id}`}
        layout={horizontal ? 'C' : 'B'}
        to={item.companyUrl}
        profileImage={{
          profileName: item.name,
          src: item.profileImageUri,
        }}
        markers={markers}
        headline={item.name}
        copy1={item.industry}
        copy2={item.location}
        onClick={onClick}
        {...rest}
      />
    </TrackVisibilitySensor>
  );
};

SimpleCompanyCard.Skeleton = SimpleCompanyCardSkeleton;
